import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  ArquivoDto,
  AtendimentoView,
  DispositivoList,
  InformacaoAtendimentoView,
} from '../../../atendimento/core/_models'
import {
  AudioPlayer,
  Button,
  FetchState,
  LoadingIndicator,
  MediaLightbox,
  MediaItem,
  useModal,
} from '../../../../../components'
import { downloadAnexo } from '../../../atendimento/core/_requests'
import { DispositivoType, IconPaths } from '../../../../../../_metronic/helpers'
import { ExamesImagem } from '../components/exames-imagem/ExamesImagem'

export type Leitura = {
  descricao: string
  anexoId: number | null
  anexo: ArquivoDto | null
  valor: number | null
  unidade: string | null
  dataFormatada: string
  data: Date
}

export type IData = {
  dispositivo?: DispositivoList | null
  leituras: Leitura[]
}

// Câmera pode capturar tanto foto quanto vídeo, então fica na mesma categoria que os
// demais dispositivos de imagem em vez de ter uma categoria "Vídeo" só para ela.
type DeviceKind = 'texto' | 'audio' | 'midia'

type DeviceEntry = {
  dispositivo: DispositivoList
  leituras: Leitura[]
}

const KIND_BY_TIPO: Record<DispositivoType, DeviceKind> = {
  [DispositivoType.Balanca]: 'texto',
  [DispositivoType.Oximetro]: 'texto',
  [DispositivoType.Termometro]: 'texto',
  [DispositivoType.Pressao]: 'texto',
  [DispositivoType.Glicosimetro]: 'texto',
  [DispositivoType.Estetoscopio]: 'audio',
  [DispositivoType.Doppler]: 'audio',
  [DispositivoType.Inspecao]: 'midia',
  [DispositivoType.Otoscopio]: 'midia',
  [DispositivoType.Dermatoscopio]: 'midia',
  [DispositivoType.Oftalmoscopio]: 'midia',
  [DispositivoType.Retinografo]: 'midia',
  [DispositivoType.Ecg]: 'midia',
  [DispositivoType.Camera]: 'midia',
}

const KIND_LABELS: Record<DeviceKind, string> = {
  texto: 'Medidas',
  audio: 'Áudio',
  midia: 'Imagem/Vídeo',
}

const KIND_ORDER: DeviceKind[] = ['texto', 'audio', 'midia']

const KIND_NOUNS: Record<DeviceKind, [string, string]> = {
  texto: ['medida', 'medidas'],
  audio: ['áudio', 'áudios'],
  midia: ['captura', 'capturas'],
}

const countLabel = (kind: DeviceKind, count: number) => {
  if (!count) return 'Sem capturas'
  const [singular, plural] = KIND_NOUNS[kind]
  return `${count} ${count === 1 ? singular : plural}`
}

const TIPO_LABELS: Record<DispositivoType, string> = {
  [DispositivoType.Balanca]: 'Balança',
  [DispositivoType.Oximetro]: 'Oxímetro',
  [DispositivoType.Termometro]: 'Termômetro',
  [DispositivoType.Pressao]: 'Pressão arterial',
  [DispositivoType.Glicosimetro]: 'Glicosímetro',
  [DispositivoType.Estetoscopio]: 'Estetoscópio',
  [DispositivoType.Doppler]: 'Doppler',
  [DispositivoType.Inspecao]: 'Inspeção',
  [DispositivoType.Otoscopio]: 'Otoscópio',
  [DispositivoType.Dermatoscopio]: 'Dermatoscópio',
  [DispositivoType.Oftalmoscopio]: 'Oftalmoscópio',
  [DispositivoType.Retinografo]: 'Retinógrafo',
  [DispositivoType.Ecg]: 'ECG',
  [DispositivoType.Camera]: 'Câmera',
}

const TODOS_TIPOS = Object.keys(KIND_BY_TIPO) as DispositivoType[]

const DISPOSITIVO_ICON_BASE: Partial<Record<DispositivoType, string>> = {
  [DispositivoType.Balanca]: 'balanca',
  [DispositivoType.Oximetro]: 'oximetro',
  [DispositivoType.Termometro]: 'termometro',
  [DispositivoType.Pressao]: 'pressao',
  [DispositivoType.Glicosimetro]: 'glicosimetro',
  [DispositivoType.Estetoscopio]: 'estetoscopio',
  [DispositivoType.Doppler]: 'doppler',
  [DispositivoType.Otoscopio]: 'otoscopio',
  [DispositivoType.Dermatoscopio]: 'dermatoscopio',
  [DispositivoType.Ecg]: 'ecg',
  [DispositivoType.Camera]: 'camera',
  [DispositivoType.Inspecao]: 'camera',
}

const getDispositivoIconSrc = (tipo: DispositivoType, comCaptura: boolean): string => {
  const base = DISPOSITIVO_ICON_BASE[tipo]
  if (!base) return IconPaths.Eye
  return `/media/icons/duotune/sinais/${base}${comCaptura ? '-on' : ''}.png`
}

type LeituraGroup = {
  dataFormatada: string
  leituras: Leitura[]
}

const agruparPorData = (leituras: Leitura[]): LeituraGroup[] => {
  const grupos: LeituraGroup[] = []

  leituras.forEach((leitura) => {
    const grupo = grupos.find((g) => g.dataFormatada === leitura.dataFormatada)
    if (grupo) {
      grupo.leituras.push(leitura)
    } else {
      grupos.push({ dataFormatada: leitura.dataFormatada, leituras: [leitura] })
    }
  })

  return grupos
}

export const DispositivosModal: FC = () => {
  const { fetchStates, refetch } = useModal()

  const { data: atendimento, isLoading: carregandoAtendimento } = (fetchStates['atendimento'] ||
    {}) as FetchState<AtendimentoView>

  const [data, setData] = useState<IData[]>([])
  const [resolvedTipos, setResolvedTipos] = useState<Set<DispositivoType>>(new Set())
  const [loadingTipo, setLoadingTipo] = useState<DispositivoType | undefined>(undefined)

  const [showCarousel, setShowCarousel] = useState(false)
  const [carouselItems, setCarouselItems] = useState<MediaItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined)

  const [selectedTipo, setSelectedTipo] = useState<DispositivoType | undefined>(undefined)

  useEffect(() => {
    if (!atendimento) return

    setData(processInformacoes(atendimento.informacoes || []))
    setResolvedTipos(new Set())
    setLoadingTipo(undefined)
  }, [atendimento])

  const processInformacoes = (informacoes: InformacaoAtendimentoView[]): IData[] => {
    const dispAux: IData[] = []

    informacoes.forEach((informacao) => {
      const leitura: Leitura = {
        descricao: informacao.descricao,
        anexoId: informacao.anexo,
        anexo: null,
        valor: informacao.valor,
        unidade: informacao.unidade,
        dataFormatada: informacao.dataFormatada,
        data: new Date(informacao.data),
      }

      const info = dispAux.find((d) => d?.dispositivo?.id === informacao.dispositivo?.id)

      if (info) {
        info.leituras.push(leitura)
      } else {
        dispAux.push({ dispositivo: informacao.dispositivo, leituras: [leitura] })
      }
    })

    return dispAux
  }

  const atendimentoId = atendimento?.id

  useEffect(() => {
    if (!selectedTipo) return
    const tipo = selectedTipo

    // Toda reexecução que `resolvedTipos`/`loadingTipo` disparam cai nesta guarda:
    // o tipo já resolvido ou em download nunca é baixado de novo.
    if (resolvedTipos.has(tipo) || loadingTipo === tipo) return

    const device = data.find((d) => d.dispositivo?.tipo === tipo)
    const temAnexoPendente = device?.leituras.some((l) => l.anexoId !== null && !l.anexo)

    if (!device || !temAnexoPendente) {
      setResolvedTipos((prev) => new Set(prev).add(tipo))
      return
    }

    const resolverAnexosDoDispositivo = async () => {
      setLoadingTipo(tipo)
      try {
        const leiturasResolvidas = await Promise.all(
          device.leituras.map(async (leitura) => {
            if (!leitura.anexoId || leitura.anexo) return leitura
            const anexo = await downloadAnexo(atendimentoId, leitura.anexoId)
            return { ...leitura, anexo }
          })
        )

        setData((prev) =>
          prev.map((d) =>
            d.dispositivo?.tipo === tipo ? { ...d, leituras: leiturasResolvidas } : d
          )
        )
      } catch (error) {
        console.error(error)
      } finally {
        setResolvedTipos((prev) => new Set(prev).add(tipo))
        setLoadingTipo(undefined)
      }
    }

    resolverAnexosDoDispositivo()
  }, [selectedTipo, data, resolvedTipos, loadingTipo, atendimentoId])

  // Parte sempre do conjunto completo de tipos de dispositivo, para que a lista nunca
  // fique vazia — mesmo quando o atendimento ainda não tem nenhuma captura registrada.
  const devices = useMemo<DeviceEntry[]>(() => {
    const byTipo = new Map<DispositivoType, DeviceEntry>()

    TODOS_TIPOS.forEach((tipo) => {
      byTipo.set(tipo, {
        dispositivo: { id: undefined, tipo, serie: '', descricao: TIPO_LABELS[tipo] },
        leituras: [],
      })
    })

    data.forEach((d) => {
      if (!d.dispositivo) return
      const existing = byTipo.get(d.dispositivo.tipo)
      if (existing) {
        existing.dispositivo = d.dispositivo
        existing.leituras = [...existing.leituras, ...d.leituras]
      } else {
        byTipo.set(d.dispositivo.tipo, { dispositivo: d.dispositivo, leituras: d.leituras })
      }
    })

    return Array.from(byTipo.values())
  }, [data])

  const groups = useMemo(
    () =>
      KIND_ORDER.map((kind) => ({
        kind,
        label: KIND_LABELS[kind],
        devices: devices.filter((d) => KIND_BY_TIPO[d.dispositivo.tipo] === kind),
      })).filter((group) => group.devices.length > 0),
    [devices]
  )

  useEffect(() => {
    if (devices.length === 0) return
    if (devices.some((d) => d.dispositivo.tipo === selectedTipo)) return

    const primeiroComDados = devices.find((d) => d.leituras.length > 0)
    setSelectedTipo((primeiroComDados ?? devices[0]).dispositivo.tipo)
  }, [devices, selectedTipo])

  const selected = devices.find((d) => d.dispositivo.tipo === selectedTipo)

  const openCarousel = (items: MediaItem[], selectedItemIndex: number) => {
    setCarouselItems(items)
    setSelectedIndex(selectedItemIndex)
    setShowCarousel(true)
  }

  const collectAnexoUrls = (leituras: Leitura[]): MediaItem[] => {
    return leituras
      .filter((leitura) => leitura.anexo)
      .map((leitura) => ({
        mediaUrl: `data:${leitura.anexo?.type};base64,${leitura.anexo?.contentBase64}`,
        title: `${leitura.descricao}: (${leitura.dataFormatada})`,
        type: leitura.anexo?.type as string,
      }))
  }

  const renderConteudoDispositivo = (device: DeviceEntry) => {
    const isAudio =
      device.dispositivo.tipo === DispositivoType.Estetoscopio ||
      device.dispositivo.tipo === DispositivoType.Doppler

    const leiturasRecentesPrimeiro = [...device.leituras].sort(
      (a, b) => b.data.getTime() - a.data.getTime()
    )

    const comAnexo = leiturasRecentesPrimeiro.filter((leitura) => leitura.anexo)
    const semAnexo = leiturasRecentesPrimeiro.filter((leitura) => !leitura.anexo)
    const mediaItems = !isAudio ? collectAnexoUrls(leiturasRecentesPrimeiro) : []

    return (
      <div className='d-flex flex-column gap-5'>
        {comAnexo.length > 0 &&
          (isAudio ? (
            <div className='d-flex flex-column'>
              {comAnexo.map((leitura, index) => (
                <div
                  key={index}
                  className='d-flex justify-content-between align-items-center border-bottom border-gray-300 py-3 gap-4'
                >
                  <div className='d-flex flex-column'>
                    <span className='fw-semibold fs-6 text-gray-800'>{leitura.descricao}</span>
                    <span className='fs-8 text-muted'>{leitura.dataFormatada}</span>
                  </div>
                  <AudioPlayer
                    src={`data:audio/mp4;base64,${leitura.anexo?.contentBase64}`}
                    fileName={`${leitura.descricao} - ${leitura.dataFormatada}.mp4`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div
              className='d-grid gap-4'
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}
            >
              {comAnexo.map((leitura, index) => {
                const isImagem =
                  leitura.anexo?.type?.includes('image') || leitura.anexo?.type?.includes('pdf')
                const mediaUrl = `data:${leitura.anexo?.type};base64,${leitura.anexo?.contentBase64}`

                return (
                  <button
                    key={index}
                    type='button'
                    className='p-0 border border-gray-300 rounded bg-body text-start overflow-hidden'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const itemIndex = mediaItems.findIndex((item) => item.mediaUrl === mediaUrl)
                      openCarousel(mediaItems, itemIndex)
                    }}
                  >
                    <div
                      className='d-flex align-items-center justify-content-center fw-bold fs-8 bg-gray-600 text-gray-100'
                      style={{ aspectRatio: '4 / 3' }}
                    >
                      {isImagem ? 'Ver imagem' : '▶ Ver vídeo'}
                    </div>
                    <div className='d-flex flex-column p-2'>
                      <span className='fw-semibold fs-8 text-gray-800 text-truncate'>
                        {leitura.descricao}
                      </span>
                      <span className='fs-9 text-muted'>{leitura.dataFormatada}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          ))}

        {semAnexo.length > 0 && (
          <div className='d-flex flex-column'>
            {agruparPorData(semAnexo).map((grupo, index) => (
              <div key={index} className='border-bottom border-gray-300 py-3'>
                <span className='fs-8 text-muted d-block mb-2'>{grupo.dataFormatada}</span>
                <div className='d-flex flex-column gap-2'>
                  {grupo.leituras.map((leitura, leituraIndex) => (
                    <div
                      key={leituraIndex}
                      className='d-flex justify-content-between align-items-baseline gap-4'
                    >
                      <span className='fw-semibold fs-6 text-gray-800'>{leitura.descricao}</span>
                      <span className='fw-bold fs-4 text-gray-900 text-nowrap'>
                        {leitura.valor} {leitura.unidade}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='d-flex flex-column' style={{ minHeight: 480 }}>
      {devices.length > 0 ? (
        <div className='d-flex border border-gray-300 rounded' style={{ minHeight: 420 }}>
          <div
            className='d-flex flex-column border-end border-gray-300 flex-shrink-0'
            style={{ width: 230 }}
          >
            {groups.map((group, groupIndex) => (
              <div key={group.kind} className='d-flex flex-column'>
                <div
                  className={`px-4 py-2 fs-9 fw-bold text-uppercase text-gray-600 bg-gray-100 ${
                    groupIndex > 0 ? 'border-top border-gray-300' : ''
                  }`}
                >
                  {group.label}
                </div>
                {group.devices.map((device, deviceIndex) => {
                  const isActive = device.dispositivo.tipo === selected?.dispositivo.tipo
                  const isEmpty = device.leituras.length === 0
                  const isLastOfGroup = deviceIndex === group.devices.length - 1
                  const kind = KIND_BY_TIPO[device.dispositivo.tipo]

                  return (
                    <button
                      key={device.dispositivo.tipo}
                      type='button'
                      className={`btn text-start rounded-0 d-flex align-items-center justify-content-between px-4 py-3 ${
                        isActive ? 'bg-gray-300' : 'bg-gray-100'
                      }`}
                      style={{
                        transition: 'none',
                        borderTop: 'none',
                        borderRight: 'none',
                        borderBottom: isLastOfGroup ? 'none' : '1px solid var(--bs-gray-200)',
                        borderLeft: `4px solid ${isActive ? 'var(--bs-primary)' : 'transparent'}`,
                      }}
                      onClick={() => setSelectedTipo(device.dispositivo.tipo)}
                    >
                      <span className='d-flex align-items-center gap-2 text-truncate'>
                        <img
                          src={getDispositivoIconSrc(device.dispositivo.tipo, !isEmpty)}
                          alt=''
                          width={18}
                          height={18}
                          style={{ objectFit: 'contain', flexShrink: 0 }}
                        />
                        <span
                          className={`fw-bold fs-7 text-truncate ${
                            isEmpty ? 'text-gray-400' : 'text-gray-800'
                          }`}
                        >
                          {device.dispositivo.descricao}
                        </span>
                      </span>
                      <span className='fs-9 text-muted text-nowrap ms-2'>
                        {countLabel(kind, device.leituras.length)}
                      </span>
                    </button>
                  )
                })}
              </div>
            ))}
          </div>

          <div className='flex-grow-1 p-5' style={{ minWidth: 0 }}>
            {carregandoAtendimento ? (
              <div className='d-flex align-items-center justify-content-center h-100 py-10'>
                <LoadingIndicator message='Carregando informações do dispositivo...' />
              </div>
            ) : (
              selected && (
                <>
                  <div className='d-flex align-items-baseline justify-content-between border-bottom border-gray-300 pb-3 mb-4 gap-4'>
                    <h4 className='mb-0 fw-bold text-gray-900'>{selected.dispositivo.descricao}</h4>
                    <div className='d-flex align-items-baseline gap-4'>
                      <span className='fs-8 text-muted text-nowrap'>
                        {selected.leituras.length > 0
                          ? countLabel(
                              KIND_BY_TIPO[selected.dispositivo.tipo],
                              selected.leituras.length
                            )
                          : 'Nenhum registro neste atendimento'}
                      </span>
                      <span className='badge badge-secondary'>
                        {KIND_LABELS[KIND_BY_TIPO[selected.dispositivo.tipo]]}
                      </span>
                    </div>
                  </div>

                  {selected.leituras.length === 0 ? (
                    <div className='d-flex flex-column gap-2' style={{ maxWidth: '44ch' }}>
                      <span className='fw-bold fs-6 text-gray-800'>
                        Nada capturado por este dispositivo
                      </span>
                      <span className='fs-7 text-muted'>
                        O dispositivo está disponível no kit, mas não foi usado neste atendimento.
                      </span>
                    </div>
                  ) : loadingTipo === selected.dispositivo.tipo ? (
                    <div className='d-flex align-items-center justify-content-center py-10'>
                      <LoadingIndicator message='Carregando mídia deste dispositivo...' />
                    </div>
                  ) : (
                    renderConteudoDispositivo(selected)
                  )}
                </>
              )
            )}
          </div>
        </div>
      ) : carregandoAtendimento ? (
        <LoadingIndicator message='Carregando dispositivos...' />
      ) : (
        <span className='text-muted'>Nenhum dispositivo disponível para este atendimento.</span>
      )}
      {atendimentoId ? <ExamesImagem idAtendimento={atendimentoId} somenteResultados /> : null}
      <div className='d-flex justify-content-end mt-3'>
        <Button
          className='btn-primary'
          size='btn-sm'
          disabled={carregandoAtendimento}
          onClick={() => refetch('atendimento')}
        >
          {carregandoAtendimento ? <LoadingIndicator /> : 'Atualizar Informações'}
        </Button>
      </div>

      <MediaLightbox
        items={carouselItems}
        initialIndex={selectedIndex}
        open={showCarousel}
        onClose={() => setShowCarousel(false)}
      />
    </div>
  )
}
